import { useState, useEffect, useCallback, useRef } from 'react'

interface ToastMessage {
  id: number
  message: string
  type: 'success' | 'error' | 'warning' | 'info'
}

let toastId = 0
const toastListeners: ((toast: ToastMessage) => void)[] = []

// Global toast function
export function showToast(
  message: string,
  type: 'success' | 'error' | 'warning' | 'info' = 'info'
): void {
  const toast: ToastMessage = { id: ++toastId, message, type }
  toastListeners.forEach((listener) => listener(toast))
}

// Toast Container Component
export function ToastContainer() {
  const [toasts, setToasts] = useState<ToastMessage[]>([])
  const timeoutsRef = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map())

  // Subscribe to toasts
  useEffect(() => {
    const handler = (toast: ToastMessage) => {
      setToasts((prev) => [...prev, toast])

      // Auto remove after 5s with cleanup tracking
      const timeoutId = setTimeout(() => {
        timeoutsRef.current.delete(toast.id)
        setToasts((prev) => prev.filter((t) => t.id !== toast.id))
      }, 5000)

      timeoutsRef.current.set(toast.id, timeoutId)
    }

    toastListeners.push(handler)

    return () => {
      // Cleanup: remove listener and clear all pending timeouts
      const idx = toastListeners.indexOf(handler)
      if (idx >= 0) toastListeners.splice(idx, 1)

      timeoutsRef.current.forEach((tid) => clearTimeout(tid))
      timeoutsRef.current.clear()
    }
  }, [])

  const removeToast = useCallback((id: number) => {
    // Clear timeout if manually dismissed
    const tid = timeoutsRef.current.get(id)
    if (tid) {
      clearTimeout(tid)
      timeoutsRef.current.delete(id)
    }
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])

  if (toasts.length === 0) return null

  return (
    <div className="toast-container">
      {toasts.map((toast) => (
        <div
          key={toast.id}
          className={`toast toast-${toast.type}`}
          onClick={() => removeToast(toast.id)}
        >
          <span className="toast-message">{toast.message}</span>
          <button className="toast-close" aria-label="Close">&times;</button>
        </div>
      ))}
    </div>
  )
}
